import { MouseEvent, useState } from 'react'
import { Typography, Menu, MenuItem, ListItemIcon, Tooltip } from '@mui/material'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { ColorTokens } from 'src/feature/theme/model/Palette'
import { PerformanceReviewTemplate } from '../../model/my-team-performance'
import { downloadPerformanceReviewPdf } from '../../api/my-team-performance-api'
import { useMyTeamStore } from '../../store/my-team.store'
import { AppSearchBar } from 'src/component/common/analog/molecule/AppSearchBar'
import CheckIcon from '@mui/icons-material/Check'
import { DeletePerformanceReviewModal } from './DeletePerformanceReviewModal'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { AppAvatar } from 'src/component/common/analog/molecule/AppAvatar'
import { API_BASE_URL } from 'src/util/constant'

type PerformanceTemplateListProps = {
  templates: PerformanceReviewTemplate[]
  selectedId: string | null
  onSelect: (id: string | null) => void
}

export function PerformanceTemplateList({ templates, selectedId, onSelect }: PerformanceTemplateListProps) {
  const myTeamStore = useMyTeamStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest' | 'name'>('newest')
  const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null)
  const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null)
  const [deleteTemplateId, setDeleteTemplateId] = useState<string | null>(null)
  const { locale, translation } = useLocalizationStore()
  const performanceReviewTranslation = translation.pages.myTeam.performanceReview
  
  const statusLabels = {
    all: performanceReviewTranslation.allReviews(),
    active: performanceReviewTranslation.active(),
    inactive: performanceReviewTranslation.completed(),
  }
  
  const handleCardClick = (id: string) => {
    onSelect(id)
  }

  const handleDownloadTemplate = async (
    template: PerformanceReviewTemplate,
  ) => {
    const data = await downloadPerformanceReviewPdf(template.id)
    const blob = new Blob([data], {
      type: 'application/pdf',
    })

    const url = window.URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = `${template.name}.pdf`

    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  }

  const handleDeleteTemplate = async (id: string) => {
    await myTeamStore.deletePerformanceReviewTemplate(id)
  }

  const requestDeleteTemplate = (id: string) => {
    setDeleteTemplateId(id)
  }

  const confirmDeleteTemplate = async () => {
    if (!deleteTemplateId) {
      return
    }

    const id = deleteTemplateId

    setDeleteTemplateId(null)

    await handleDeleteTemplate(id)
  }

  const cancelDeleteTemplate = () => {
    setDeleteTemplateId(null)
  }

  const openStatusMenu = (e: MouseEvent<HTMLDivElement>) => {
    setStatusAnchor(e.currentTarget)
  }

  const openSortMenu = (e: MouseEvent<HTMLDivElement>) => {
    setSortAnchor(e.currentTarget)
  }

  const pickStatus = (value: 'all' | 'active' | 'inactive') => {
    setStatusFilter(value)
    setStatusAnchor(null)
  }

  const pickSort = (value: 'newest' | 'oldest' | 'name') => {
    setSortOrder(value)
    setSortAnchor(null)
  }

  const matchesStatus = (template: PerformanceReviewTemplate) => {
    if (statusFilter === 'active') {
      return template.isActive
    }

    if (statusFilter === 'inactive') {
      return !template.isActive
    }

    return true
  }

  const filteredTemplates = templates
    .filter(template => matchesStatus(template) && template.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortOrder === 'name') {
        return a.name.localeCompare(b.name)
      }

      if (sortOrder === 'oldest') {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      }

      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })

  const formatDate = (date?: string) => {
    return date
      ? new Date(date).toLocaleDateString(
        locale === 'sv-SE' ? 'sv-SE' : 'en-US',
        {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        },
      ) : '-'
  }

  return (
    <AppPaper sx={styles.container}>
      <AppBox sx={styles.toolbarRow}>
        <AppSearchBar
          onChange={e => setSearchQuery(e.target.value)}
          sx={styles.searchBar}
        />

        <AppBox sx={styles.toolbarControls}>
          <AppBox>
            <AppBox
              component='button'
              type='button'
              sx={styles.dropdownButton}
              onClick={openStatusMenu}
              aria-controls={statusAnchor ? 'status-filter-menu' : undefined}
              aria-expanded={statusAnchor ? 'true' : undefined}
              aria-haspopup='menu'
            >
              <Typography sx={styles.dropdownText}>
                {statusLabels[statusFilter]}
              </Typography>

              {statusAnchor
                ? <KeyboardArrowUpRoundedIcon fontSize='small' sx={{ color: 'inherit' }} />
                : <KeyboardArrowDownRoundedIcon fontSize='small' sx={{ color: 'inherit' }} />}
            </AppBox>
            <Menu
              anchorEl={statusAnchor}
              open={Boolean(statusAnchor)}
              onClose={() => setStatusAnchor(null)}
            >
              <Typography sx={styles.menuSectionTitle}>
                {performanceReviewTranslation.filter()}
              </Typography>
              <MenuItem
                onClick={() => pickStatus('all')}
                sx={styles.menuItem}
              >
                <ListItemIcon>
                  {statusFilter === 'all' && (
                    <CheckIcon fontSize='small' sx={{ color: 'text.primary' }}/>
                  )}
                </ListItemIcon>
                {performanceReviewTranslation.allReviews()}
              </MenuItem>

              <MenuItem
                onClick={() => pickStatus('active')}
                sx={styles.menuItem}
              >
                <ListItemIcon>
                  {statusFilter === 'active' && (
                    <CheckIcon fontSize='small' sx={{ color: 'text.primary' }}/>
                  )}
                </ListItemIcon>
                {performanceReviewTranslation.tabActive()}
              </MenuItem>

              <MenuItem
                onClick={() => pickStatus('inactive')}
                sx={styles.menuItem}
              >
                <ListItemIcon>
                  {statusFilter === 'inactive' && (
                    <CheckIcon fontSize='small' sx={{ color: 'text.primary' }}/>
                  )}
                </ListItemIcon>
                {performanceReviewTranslation.tabCompleted()}
              </MenuItem>
            </Menu>
          </AppBox>
          <AppBox>
            <AppBox
              component='button'
              type='button'
              sx={styles.dropdownButton}
              onClick={openSortMenu}
              aria-controls={sortAnchor ? 'sort-menu' : undefined}
              aria-expanded={sortAnchor ? 'true' : undefined}
              aria-haspopup='menu'
            >
              <Typography sx={styles.dropdownText}>
                {performanceReviewTranslation.sortBy()}
              </Typography>

              {sortAnchor
                ? <KeyboardArrowUpRoundedIcon fontSize='small' sx={{ color: 'inherit' }} />
                : <KeyboardArrowDownRoundedIcon fontSize='small' sx={{ color: 'inherit' }} />}
            </AppBox>
            <Menu
              anchorEl={sortAnchor}
              open={Boolean(sortAnchor)}
              onClose={() => setSortAnchor(null)}
            >
              <Typography sx={styles.menuSectionTitle}>
                {performanceReviewTranslation.sortBy()}
              </Typography>

              <MenuItem
                onClick={() => pickSort('newest')}
                sx={styles.menuItem}
              >
                <ListItemIcon>
                  {sortOrder === 'newest' && (
                    <CheckIcon fontSize='small' sx={{ color: 'text.primary' }}/>
                  )}
                </ListItemIcon>
                {performanceReviewTranslation.sortNewest()}
              </MenuItem>

              <MenuItem
                onClick={() => pickSort('oldest')}
                sx={styles.menuItem}
              >
                <ListItemIcon>
                  {sortOrder === 'oldest' && (
                    <CheckIcon fontSize='small' sx={{ color: 'text.primary' }}/>
                  )}
                </ListItemIcon>
                {performanceReviewTranslation.sortOldest()}
              </MenuItem>

              <MenuItem
                onClick={() => pickSort('name')}
                sx={styles.menuItem}
              >
                <ListItemIcon>
                  {sortOrder === 'name' && (
                    <CheckIcon fontSize='small' sx={{ color: 'text.primary' }}/>
                  )}
                </ListItemIcon>
                {performanceReviewTranslation.sortName()}
              </MenuItem>
            </Menu>
          </AppBox>
        </AppBox>
      </AppBox>

      <AppBox sx={styles.cardList}>
        {filteredTemplates.length === 0 ? (
          <Typography sx={styles.emptyText}>
            {templates.length === 0
              ? performanceReviewTranslation.noActiveTemplates()
              : performanceReviewTranslation.noMatches()}
          </Typography>
        ) : (
          filteredTemplates.map(template => (
            <AppBox key={template.id} sx={styles.cardRow}>
              <AppBox
                sx={[styles.card,
                  template.id === selectedId && styles.cardSelected]}
                onClick={() => handleCardClick(template.id)}
              >
                <AppBox
                  sx={styles.leftBar(template.id === selectedId)}
                />
                <AppBox sx={styles.cardTopRow}>
                  <AppBox sx={styles.statusPill}>
                    {template.isActive
                      ? performanceReviewTranslation.tabActive()
                      : performanceReviewTranslation.tabCompleted()}
                  </AppBox>
                  <AppBox sx={styles.cardActions}>
                    <AppBox
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: ColorTokens.secondary100,
                        borderRadius: '999px',
                        width: 30,
                        height: 30,
                      }}>
                      <FileDownloadOutlinedIcon
                        fontSize='small'
                        sx={styles.downloadIcon}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDownloadTemplate(template)
                        }}
                      />
                    </AppBox>
                    <AppBox
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: ColorTokens.red10,
                        borderRadius: '999px',
                        width: 30,
                        height: 30,
                      }}
                    >
                      <DeleteOutlineOutlinedIcon
                        fontSize='small'
                        sx={styles.trashIcon}
                        onClick={(e) => {
                          e.stopPropagation()
                          requestDeleteTemplate(template.id)
                        }}
                      />
                    </AppBox>
                  </AppBox>
                </AppBox>
                <Tooltip title={template.name} placement='top' arrow>
                  <Typography variant='h4' noWrap>{template.name}</Typography>
                </Tooltip>
                <AppBox sx={styles.cardDivider} />
                <AppBox sx={styles.cardBottomRow}>
                  <AppBox>
                    {template.participants.slice(0, 4).map(user => (
                      <AppBox
                        key={user.id}
                        sx={styles.avatarWrapper}
                      >
                        <AppAvatar
                          src={user.avatarUrl ? `${API_BASE_URL}${user.avatarUrl}` : undefined}
                          outerProps={{
                            squareSize: 40,
                            sx: styles.avatar,
                          }}
                        />
                      </AppBox>
                    ))}
                  </AppBox>
                  <Typography sx={styles.cardDate}>{formatDate(template.dueDate)}</Typography>
                </AppBox>
              </AppBox>
            </AppBox>
          ))
        )}
      </AppBox>
      <DeletePerformanceReviewModal
        open={deleteTemplateId !== null}
        templateName={
          templates.find(template => template.id === deleteTemplateId)?.name
        }
        onClose={cancelDeleteTemplate}
        onConfirm={confirmDeleteTemplate}
      />
    </AppPaper>
  )
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 15,
    padding: '10px 15px',
    borderRadius: '12px',
    border: 'none',
  },
  searchBar: {
    flex: 1,
    minWidth: 0,
    border: 0,
    backgroundColor: ColorTokens.primary10,
    input: {
      fontSize: '12px',
    },
    '&:hover': {
      border: 0,
    },
    '&.Mui-focused': {
      border: 0,
    },
    '&.Mui-focused:hover': {
      border: 0,
    },
    '&:active': {
      border: 0,
    },
  },
  filterRow: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  toolbarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    flexWrap: 'nowrap',
    paddingBottom: '5px',
  },
  toolbarControls: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
  },
  dropdownButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: 12,
    border: '1px solid',
    borderColor: 'primary.main',
    backgroundColor: 'primary.main',
    color: 'background.default',
    padding: '5px 10px',
    borderRadius: '10px',
  },
  dropdownText: {
    fontSize: 12,
    fontWeight: 400,
    color: 'inherit',
  },
  menuSectionTitle: {
    color: 'text.secondary',
    padding: '0.5rem 1rem',
    fontSize: 12,
  },
  menuItem: {
    fontSize: 12,
    '& .MuiListItemIcon-root': {
      minWidth: 24,
    },
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    paddingRight: '5px',
    paddingTop: '5px',
  },
  trashIcon: {
    color: ColorTokens.red200,
    cursor: 'pointer',
  },
  downloadIcon: {
    color: ColorTokens.secondary500,
    cursor: 'pointer',
  },
  cardList: {
    width: '100%',
    flexDirection: 'column',
    gap: 10,
  },
  cardRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  card: {
    width: '100%',
    position: 'relative',
    flexDirection: 'column',
    gap: 10,
    padding: '5px 10px 15px 15px',
    borderRadius: '12px',
    borderColor: 'transparent',
    backgroundColor: 'background.dark',
    overflow: 'hidden',
    '&:hover': {
      cursor: 'pointer',
      borderColor: ColorTokens.primary10,
    },
    boxSizing: 'border-box',
    cursor: 'pointer',
  },
  leftBar: (isSelected: boolean) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '5px',
    height: '100%',
    backgroundColor: isSelected
      ? ColorTokens.primary200
      : ColorTokens.secondary500,
    transition: 'background-color 0.2s ease',
  }),
  cardSelected: {
    backgroundColor: ColorTokens.primary10,
  },
  avatarWrapper: {
    marginLeft: '-15px',
    '&:first-of-type': {
      marginLeft: 0,
    },
  },
  avatar: {
    border: `1px solid ${ColorTokens.neutral100}`,
    borderRadius: '50%',
  },
  cardTopRow: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statusPill: {
    padding: '3px 7px',
    borderRadius: '20px',
    fontSize: 12,
    backgroundColor: ColorTokens.primary200,
  },
  cardDivider: {
    width: '100%',
    height: '1px',
    backgroundColor: ColorTokens.neutral200,
  },
  cardBottomRow: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardDate: {
    fontSize: 12,
    color: 'text.secondary',
    paddingRight: '5px',
  },
  emptyText: {
    fontSize: 12,
    color: 'text.secondary',
    textAlign: 'center',
    padding: 20,
  },
}
